import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink, HeartHandshake, Info } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { loadScenarioById, type ScenarioDetail, type ScenarioDetailProcedure } from '@/lib/scenarioDetailStore';
import { useStateInternationalStats } from '@/hooks/useStateInternationalStats';

// ── Theme interface (matches ahpC from HospitalRankings) ──────
export interface DashboardTheme {
  cardBg: (a: number) => string;
  borderCls: string;
  borderAt: (a: number) => string;
  text: string;
  textAt: (a: number) => string;
  isAhp: boolean;
  isDark: boolean;
}

// ── Code type badge colors ────────────────────────────────────
const CODE_TYPE_COLORS: Record<string, string> = {
  CPT: '#3b82f6',
  HCPCS: '#8b5cf6',
  'MS-DRG': '#f59e0b',
  DRG: '#f59e0b',
  REV: '#10b981',
  NDC: '#ec4899',
  LOCAL: '#6b7280',
};

// Patriotic phase colors (matching ScenarioCard)
const PHASE_COLORS = ['var(--civic-red)', 'var(--civic-navy)', 'var(--civic-gold)', 'var(--civic-money)', '#6B4C8A', '#D4763B'];

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ── Shared card wrapper ───────────────────────────────────────
function DashCard({ children, theme, className = '' }: {
  children: React.ReactNode;
  theme: DashboardTheme;
  className?: string;
}) {
  return (
    <div
      className={`backdrop-blur-sm rounded-lg p-3 border ${theme.borderCls} ${className}`}
      style={{ backgroundColor: theme.cardBg(0.8), minHeight: 120 }}
    >
      {children}
    </div>
  );
}

function CardLabel({ text, theme }: { text: string; theme: DashboardTheme }) {
  return (
    <h3 className="text-[10px] uppercase tracking-wider font-semibold mb-2"
        style={{ color: theme.textAt(0.4) }}>
      {text}
    </h3>
  );
}

// ── Combined Scenario + Phase Card (matches ScenarioCard design) ──
export function DashboardScenarioCard({
  theme, scenarioMeta, activeScenario, scenarioId, scenarioDetail, onViewVariants, familySize,
}: {
  theme: DashboardTheme;
  scenarioMeta: { name: string; description?: string; nationalTotal: number; procedureCount: number } | null;
  activeScenario: any;
  scenarioId: string;
  scenarioDetail: ScenarioDetail | null | undefined;
  /** Called when user clicks "View Variants" — opens the family modal */
  onViewVariants?: () => void;
  /** Number of family members (including this scenario). Show variants button when > 1 */
  familySize?: number;
}) {
  const { t } = useTranslation(['rankings', 'common']);

  if (!scenarioMeta) return <DashCard theme={theme}><div className="animate-pulse h-full" /></DashCard>;

  const accent = theme.isAhp ? 'var(--civic-gold)' : '#00ff41';
  const red = 'var(--civic-red)';
  const breakdown = scenarioDetail?.phaseBreakdown || {};
  const phases = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  const maxPhaseCost = phases.length > 0 ? phases[0][1] : 1;

  return (
    <DashCard theme={theme}>
      {/* Name */}
      <p className="leading-snug mb-2"
         style={{
           fontFamily: serif,
           fontSize: 'clamp(15px, 2vw, 18px)',
           fontWeight: 400,
           color: theme.text,
           display: '-webkit-box',
           WebkitLineClamp: 2,
           WebkitBoxOrient: 'vertical',
           overflow: 'hidden',
         } as React.CSSProperties}>
        {scenarioMeta.name}
      </p>

      {/* Cost + Duration + Procedures row */}
      <div className="flex items-end justify-between mb-3">
        <div>
          <p style={{ fontSize: 9, color: theme.textAt(0.4), letterSpacing: '0.5px', textTransform: 'uppercase' as const }}>
            {t('dashboard.estimatedCost')}
          </p>
          <p className="flex items-baseline gap-0.5" style={{ fontFamily: serif }}>
            <span style={{ fontSize: 12, color: red }}>$</span>
            <span style={{ fontSize: 'clamp(20px, 2.5vw, 26px)', color: red, fontWeight: 400 }}>
              {Math.round(scenarioMeta.nationalTotal).toLocaleString()}
            </span>
          </p>
        </div>
        <div className="flex gap-4 text-right">
          {scenarioDetail?.duration && (
            <div>
              <p style={{ fontSize: 9, color: theme.textAt(0.4), letterSpacing: '0.4px', textTransform: 'uppercase' as const }}>{t('dashboard.duration')}</p>
              <p style={{ fontSize: 12, fontFamily: sans, color: theme.textAt(0.7), marginTop: 1 }}>{scenarioDetail.duration}</p>
            </div>
          )}
          <div>
            <p style={{ fontSize: 9, color: theme.textAt(0.4), letterSpacing: '0.4px', textTransform: 'uppercase' as const }}>{t('dashboard.procedures')}</p>
            <p style={{ fontSize: 12, fontFamily: sans, color: 'var(--civic-navy)', marginTop: 1 }}>
              {scenarioMeta.procedureCount || activeScenario?.total_procedures || '?'}
            </p>
          </div>
        </div>
      </div>

      {/* Phase bars (matching ScenarioCard layout) */}
      {phases.length > 0 && (
        <div className="space-y-1 mb-2">
          {phases.slice(0, 4).map(([phase, cost], idx) => {
            const pct = Math.max((cost / maxPhaseCost) * 100, 3);
            return (
              <div key={phase} className="flex items-center gap-1.5">
                <span className="text-right flex-shrink-0 truncate"
                      style={{ width: 72, color: theme.textAt(0.5), fontSize: 10 }}>
                  {phase}
                </span>
                <div className="flex-1 h-3 rounded-sm" style={{ backgroundColor: theme.borderAt(0.06) }}>
                  <div className="h-full rounded-sm"
                       style={{ width: `${pct}%`, backgroundColor: PHASE_COLORS[idx % PHASE_COLORS.length], minWidth: 3 }} />
                </div>
                <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: theme.textAt(0.5), width: 40, textAlign: 'right' as const, flexShrink: 0 }}>
                  ${cost >= 1000 ? `${(cost / 1000).toFixed(0)}k` : Math.round(cost).toLocaleString()}
                </span>
              </div>
            );
          })}
          {phases.length > 4 && (
            <p style={{ fontSize: 10, color: theme.textAt(0.35), textAlign: 'right' as const }}>
              {t('dashboard.morePhases', { count: phases.length - 4 })}
            </p>
          )}
        </div>
      )}

      {/* Action links — all on one row */}
      <div className="flex items-center gap-3 flex-wrap mt-1">
        {activeScenario?.slug && (
          <Link
            to={`/scenarios/${scenarioId}/${activeScenario.slug}`}
            className="text-[11px] font-medium flex items-center gap-0.5 hover:underline"
            style={{ color: accent }}
          >
            Scenario Breakdown <ExternalLink className="w-3 h-3" />
          </Link>
        )}
        {onViewVariants && (familySize || 0) > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onViewVariants}
                className="text-[11px] font-medium flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors hover:opacity-80"
                style={{ backgroundColor: `${red}12`, color: red, border: `1px solid ${red}25` }}
              >
                Severity Variants ({familySize})
                <Info className="w-2.5 h-2.5" style={{ opacity: 0.5 }} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs" style={{ zIndex: 9999 }}>
              <p className="text-sm">View all severity and complication variants of this scenario — from uncomplicated to critical. Each variant tracks different procedure codes and costs based on clinical complexity.</p>
            </TooltipContent>
          </Tooltip>
        )}
        <button
          onClick={() => {
            const el = document.getElementById('care-resources-section');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="text-[11px] font-medium flex items-center gap-1 hover:underline"
          style={{ color: 'var(--civic-money)' }}
        >
          <HeartHandshake className="w-3 h-3" /> Connect to Care
        </button>
      </div>
    </DashCard>
  );
}

// ── Code type → route path mapping ────────────────────────────
const CODE_TYPE_ROUTE: Record<string, string> = {
  CPT: '/cpt',
  HCPCS: '/hcpcs',
  'MS-DRG': '/drg',
  DRG: '/drg',
  REV: '/revenue',
  REVENUE: '/revenue',
  NDC: '/ndc',
  DRUG: '/drug',
  SUPPLY: '/supply',
};

// ── Setting display helpers ──────────────────────────────────
const SETTING_COLORS: Record<string, string> = {
  outpatient: '#3b82f6',
  inpatient:  '#f59e0b',
  both:       '#8b5cf6',
};
const SETTING_I18N_KEYS: Record<string, string> = {
  outpatient: 'dashboard.settingOutpatientShort',
  inpatient:  'dashboard.settingInpatientShort',
  both:       'dashboard.settingBothShort',
};

// ── Procedure Codes Card ──────────────────────────────────────
export function DashboardProcedureCodes({
  theme, scenarioDetail,
}: {
  theme: DashboardTheme;
  scenarioDetail: ScenarioDetail | null | undefined;
}) {
  const { t } = useTranslation(['rankings', 'common']);

  if (!scenarioDetail?.procedures) {
    return (
      <DashCard theme={theme}>
        <CardLabel text={t('dashboard.procedureCodes')} theme={theme} />
        <div className="flex gap-2 flex-wrap">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-5 w-16 rounded animate-pulse" style={{ backgroundColor: theme.borderAt(0.1) }} />
          ))}
        </div>
      </DashCard>
    );
  }

  const pricing = scenarioDetail.nationalPricing || {};

  // Group by code type
  const codeTypeCounts: Record<string, number> = {};
  for (const proc of scenarioDetail.procedures) {
    const type = proc.codeType || 'OTHER';
    codeTypeCounts[type] = (codeTypeCounts[type] || 0) + 1;
  }

  // All procedures sorted by cost, with pricing lookup
  const allProcs = [...scenarioDetail.procedures]
    .sort((a, b) => (b.estimatedCost || 0) - (a.estimatedCost || 0));

  // Look up national pricing for a procedure
  const getPricing = (proc: ScenarioDetailProcedure) => {
    const ct = (proc.codeType || '').toUpperCase();
    const code = proc.code || '';
    const setting = proc.setting || 'unspecified';
    // Try exact setting, then unspecified, then all_settings
    for (const s of [setting, 'unspecified', 'all_settings']) {
      const key = `${ct}:${code}:${s}`;
      if (pricing[key]) return pricing[key];
    }
    return null;
  };

  return (
    <DashCard theme={theme}>
      <CardLabel text={t('dashboard.procedureCodes')} theme={theme} />
      {/* Code type summary badges */}
      <div className="flex gap-1.5 flex-wrap mb-2">
        {Object.entries(codeTypeCounts)
          .sort((a, b) => b[1] - a[1])
          .map(([type, count]) => (
            <span key={type} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{
              backgroundColor: `${CODE_TYPE_COLORS[type] || '#6b7280'}20`,
              color: CODE_TYPE_COLORS[type] || '#6b7280',
              border: `1px solid ${CODE_TYPE_COLORS[type] || '#6b7280'}40`,
            }}>
              {type}: {count}
            </span>
          ))}
      </div>
      {/* Procedure list with national pricing */}
      <div className="space-y-0.5" style={{ maxHeight: 180, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        {allProcs.map((p, i) => {
          const np = getPricing(p);
          const ct = (p.codeType || '').toUpperCase();
          const routeBase = CODE_TYPE_ROUTE[ct];
          const settingKey = (np?.s || p.setting || '').toLowerCase();
          const settingColor = SETTING_COLORS[settingKey];
          const settingI18nKey = SETTING_I18N_KEYS[settingKey];
          const codeColor = CODE_TYPE_COLORS[ct] || '#6b7280';

          return (
            <div key={i} className="flex items-center gap-1.5 py-0.5 group" style={{ borderBottom: `1px solid ${theme.borderAt(0.04)}` }}>
              {/* Code badge + link */}
              <div className="shrink-0 flex items-center gap-1">
                {routeBase ? (
                  <Link
                    to={`${routeBase}/${p.code}`}
                    className="text-[9px] font-mono font-bold px-1 py-0.5 rounded hover:underline"
                    style={{ backgroundColor: `${codeColor}15`, color: codeColor, border: `1px solid ${codeColor}30` }}
                    title={t('dashboard.viewCodeBreakdown', { type: ct, code: p.code })}
                  >
                    {p.code}
                  </Link>
                ) : (
                  <span className="text-[9px] font-mono font-bold px-1 py-0.5 rounded"
                    style={{ backgroundColor: `${codeColor}15`, color: codeColor, border: `1px solid ${codeColor}30` }}>
                    {p.code}
                  </span>
                )}
              </div>
              {/* Description — truncated */}
              <span className="flex-1 min-w-0 truncate text-[10px]" style={{ color: theme.textAt(0.55) }}>
                {np?.n || p.description}
              </span>
              {/* Setting badge */}
              {settingColor && settingI18nKey && (
                <span className="shrink-0 text-[8px] font-bold px-1 py-0.5 rounded"
                  style={{ backgroundColor: `${settingColor}15`, color: settingColor, border: `1px solid ${settingColor}25` }}>
                  {t(settingI18nKey)}
                </span>
              )}
              {/* Avg / Median prices */}
              {np ? (
                <span className="shrink-0 text-[10px] font-mono tabular-nums" style={{ color: theme.textAt(0.7) }}
                  title={np.m ? t('dashboard.priceTooltipWithMedian', { avg: np.p.toLocaleString(), median: np.m.toLocaleString() }) : t('dashboard.priceTooltip', { avg: np.p.toLocaleString() })}>
                  ${np.p >= 1000 ? `${(np.p / 1000).toFixed(np.p >= 10000 ? 0 : 1)}k` : np.p.toLocaleString()}
                  {np.m != null && np.m !== np.p && (
                    <span style={{ color: theme.textAt(0.4) }}>
                      /{np.m >= 1000 ? `${(np.m / 1000).toFixed(np.m >= 10000 ? 0 : 1)}k` : np.m.toLocaleString()}
                    </span>
                  )}
                </span>
              ) : (
                <span className="shrink-0 text-[10px] font-mono tabular-nums" style={{ color: theme.textAt(0.3) }}>—</span>
              )}
              {/* Hospital count */}
              {np && np.h > 0 && (
                <span className="shrink-0 text-[8px] tabular-nums" style={{ color: theme.textAt(0.3) }} title={t('dashboard.hospitalsWithPricingData', { count: np.h.toLocaleString() })}>
                  {np.h >= 1000 ? `${(np.h / 1000).toFixed(1)}k` : np.h}{t('dashboard.hospitalCountSuffix')}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {/* Legend + API link */}
      <div className="flex items-center justify-between mt-1.5 pt-1" style={{ borderTop: `1px solid ${theme.borderAt(0.06)}` }}>
        <span className="text-[8px]" style={{ color: theme.textAt(0.3) }}>
          {t('dashboard.avgMed')} · {t('dashboard.codesCount', { count: allProcs.length })}
        </span>
        <Link
          to="/api"
          className="text-[9px] font-medium flex items-center gap-0.5 hover:underline"
          style={{ color: theme.isAhp ? 'var(--civic-gold)' : '#00ff41' }}
        >
          {t('dashboard.api')} <ExternalLink className="w-2.5 h-2.5" />
        </Link>
      </div>
    </DashCard>
  );
}

// ── International Overlay (embedded inside map card) ──────────
export function InternationalOverlay({
  theme, scenarioId, usCost,
}: {
  theme: DashboardTheme;
  scenarioId: string;
  usCost: number;
}) {
  const { t } = useTranslation(['rankings', 'common']);
  const { scenarioCosts, countries, isLoading } = useStateInternationalStats();

  const thisScenarioCosts = scenarioCosts
    .filter(sc => sc.scenario_id === scenarioId && sc.intl_total_cost > 0)
    .sort((a, b) => a.intl_total_cost - b.intl_total_cost)
    .slice(0, 5);

  if (isLoading) {
    return (
      <div className="space-y-2 p-1">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-3 rounded animate-pulse" style={{ backgroundColor: theme.borderAt(0.1), width: `${70 + i * 10}%` }} />
        ))}
      </div>
    );
  }

  if (thisScenarioCosts.length === 0) {
    return (
      <p className="text-[10px] text-center py-4" style={{ color: theme.textAt(0.35) }}>
        {t('dashboard.noInternationalPricing')}
      </p>
    );
  }

  const usTotal = usCost || thisScenarioCosts[0]?.us_national_total_cost || 0;

  return (
    <div className="space-y-1.5">
      {/* US row */}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] w-[60px] text-right shrink-0 truncate" style={{ color: theme.textAt(0.7) }}>{t('dashboard.usLabel')}</span>
        <div className="flex-1 h-3 rounded-sm" style={{ backgroundColor: theme.borderAt(0.06) }}>
          <div className="h-full rounded-sm" style={{ width: '100%', backgroundColor: 'var(--civic-red)', opacity: 0.7 }} />
        </div>
        <span className="text-[10px] font-mono tabular-nums shrink-0 w-[48px] text-right" style={{ color: 'var(--civic-red)' }}>
          ${usTotal >= 1000 ? `${(usTotal / 1000).toFixed(1)}k` : usTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>
      {/* International rows */}
      {thisScenarioCosts.map(sc => {
        const country = countries.get(sc.country_code);
        const flag = country?.flag_emoji || '';
        const name = country?.country_name || sc.country_code;
        const barWidth = usTotal > 0 ? (sc.intl_total_cost / usTotal) * 100 : 0;
        const multiplier = usTotal > 0 ? usTotal / sc.intl_total_cost : 1;

        return (
          <div key={sc.country_code} className="flex items-center gap-1.5">
            <span className="text-[10px] w-[60px] text-right shrink-0 truncate" style={{ color: theme.textAt(0.5) }}>
              {flag} {name.length > 6 ? name.slice(0, 6) : name}
            </span>
            <div className="flex-1 h-2.5 rounded-sm" style={{ backgroundColor: theme.borderAt(0.06) }}>
              <div className="h-full rounded-sm" style={{ width: `${barWidth}%`, backgroundColor: 'var(--civic-money)', opacity: 0.7, minWidth: 3 }} />
            </div>
            <span className="text-[9px] font-mono tabular-nums shrink-0 w-[48px] text-right" style={{ color: theme.textAt(0.5) }}>
              ${sc.intl_total_cost >= 1000 ? `${(sc.intl_total_cost / 1000).toFixed(1)}k` : sc.intl_total_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
            <span className="text-[8px] font-bold shrink-0" style={{ color: 'var(--civic-money)' }}>
              {multiplier.toFixed(1)}x
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Hook: load scenario detail for dashboard cards ────────────
export function useScenarioDetail(scenarioId: string | null | undefined) {
  return useQuery({
    queryKey: ['scenario-detail-dashboard', scenarioId],
    queryFn: () => loadScenarioById(scenarioId!),
    enabled: !!scenarioId,
    staleTime: 48 * 60 * 60 * 1000,
  });
}
